import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Stethoscope, ShieldCheck, Microscope, HeartPulse, Plus, Minus } from "lucide-react";
import DoctorImg from "../assets/images/about/01.jpeg";
import Banner from '../assets/images/banner/banner.jpg';
import Service1 from '../assets/images/service/01.jpg';
import Service2 from '../assets/images/service/02.jpg';
import Service3 from '../assets/images/service/03.jpg';
import Service4 from '../assets/images/service/04.jpg';
import Service5 from '../assets/images/service/05.jpg';
import Service6 from '../assets/images/service/06.jpg';
import Service7 from '../assets/images/service/09.jpg';
import Service8 from '../assets/images/service/10.jpg';
import Service9 from '../assets/images/service/11.jpg';
import AppointmentBanner from '../assets/images/banner/appointment_banner.jpg';
import ExperBanner from '../assets/images/banner/expert_banner.jpg';
import Counter from '../components/Counter';

const Home = () => {
  const location = useLocation();

  const [open, setOpen] = useState<number | null>(0);

  useEffect(() => {
    if (location.hash === "#appointment") {
      const el = document.getElementById("appointment");
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="bg-lightBg min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/appointment">
            <img
              src={Banner}
              alt="Dr. Sachitanand Chaurasia Banner"
              className="w-full h-auto object-cover pt-24 lg:pt-28"
            />
          </Link>
        </div>

      </section>

      {/* ABOUT DOCTOR */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 px-6 items-center">

          {/* IMAGE */}
          <div
            className="relative"
          >
            <div className="absolute -top-10 -left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />

            <img
              src={DoctorImg}
              className="relative z-10 rounded-[40px] shadow-premium"
            />
          </div>

          {/* CONTENT */}
          <div>
            <p className="text-secondary font-bold uppercase tracking-widest mb-3">
              About Doctor
            </p>

            <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
              Dr. Sachitanand Chaurasia
            </h2>

            <p className="text-primary font-bold mt-2 text-lg">
              Urologist & Kidney Transplant Surgeon
            </p>

            <p className="text-slate-600 mt-6 leading-relaxed">
              Dr. Sachitanand Chaurasia is a highly experienced Consultant Urologist & Andrologist with over 8+ years of expertise in advanced urological care. He specializes in the diagnosis and treatment of kidney stones, prostate disorders, urinary tract infections, male infertility, and other urological conditions using modern, minimally invasive techniques.
            </p>
            <p className="text-slate-600 mt-6 leading-relaxed">
              With thousands of successful urological procedures, Dr. Chaurasia is known for his precision, compassionate care, and patient-first approach. His focus is on providing safe, effective treatment with faster recovery and long-term results.
            </p>
            <p className="text-slate-600 mt-6 leading-relaxed">
              He is committed to delivering personalized care tailored to each patient’s condition, ensuring comfort, transparency, and the highest standards of medical excellence.
            </p>

            <div className="grid grid-cols-2 gap-6 mt-8 mb-10">

              <div>
                <h3 className="text-3xl font-black text-primary">
                  <Counter value={8} suffix="+" />
                </h3>
                <p className="text-slate-500">Years Experience</p>
              </div>

              <div>
                <h3 className="text-3xl font-black text-primary">
                  <Counter value={10000} suffix="+" />
                </h3>
                <p className="text-slate-500">Successful Patient Treatments</p>
              </div>
            </div>

            <a href="#appointment" className="mt-10 bg-primary hover:bg-primaryDark text-white px-8 py-4 rounded-xl font-bold shadow-glow">
              Book Consultation
            </a>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 bg-lightBg">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl font-black text-slate-900 mb-4">
            Why Choose Dr. Sachitanand?
          </h2>
          <p className="text-slate-600 mb-16">
            Advanced urology care with modern technology and expert surgical precision
          </p>

          <div className="grid md:grid-cols-4 gap-8">

            {[
              { icon: Stethoscope, title: "Advanced Treatment" },
              { icon: Microscope, title: "Modern Technology" },
              { icon: ShieldCheck, title: "Trusted Surgeon" },
              { icon: HeartPulse, title: "Patient Care" },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -10 }}
                className="bg-white rounded-3xl p-8 shadow-premium hover:shadow-glow transition"
              >
                <item.icon className="mx-auto text-primary mb-4" size={40} />
                <h4 className="font-bold text-lg">{item.title}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="py-24 bg-[#f6f8fb]">
        <div className="max-w-7xl mx-auto px-6">

          {/* heading */}
          <h2 className="text-4xl font-black text-center text-slate-900 mb-4">
            Treatments Offered by Best Urologist in Bhilwara
          </h2>

          <p className="text-slate-600 mb-16 text-center">
            Advanced urology treatments with modern technology and expert surgical care.
          </p>

          {/* grid */}
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                title: "Laser Kidney Stone",
                img: Service1,
                desc: "Advanced laser kidney stone treatment with minimal pain and faster recovery by Dr. Sachitanand Chaurasia.",
                link: "/treatments/laser-kidney-stone"
              },
              {
                title: "Laparoscopic Urology",
                img: Service2,
                desc: "Minimally invasive laparoscopic urology procedures for quick healing and better outcomes.",
                link: "/treatments/laparoscopic-urology"
              },
              {
                title: "Hemodialysis & AV Fistula Creation",
                img: Service3,
                desc: "Advanced hemodialysis access and AV fistula creation for safe, long-term dialysis treatment and improved patient care.",
                link: "/treatments/hemodialysis-av-fistula"
              },
              {
                title: "Endourology Treatment",
                img: Service4,
                desc: "Non-invasive treatment for kidney, bladder and urinary tract problems.",
                link: "/treatments/endourology"
              },
              {
                title: "Prostate Enlargement",
                img: Service5,
                desc: "Advanced treatment for prostate enlargement with modern technology.",
                link: "/treatments/prostate-enlargement"
              },
              {
                title: "Urologic Oncology",
                img: Service6,
                desc: "Complete treatment for urological cancers including surgery and care.",
                link: "/treatments/urologic-oncology"
              },
              {
                title: "Andrology (Male Sexual & Fertility Health)",
                img: Service7,
                desc: "Advanced diagnosis and treatment for erectile dysfunction, male infertility, ejaculation disorders and hormonal issues with modern andrology procedures.",
                link: "/treatments/andrology"
              },

              {
                title: "Female Urology",
                img: Service8,
                desc: "Specialized care for urinary leakage, recurrent UTI, incontinence and urinary fistula with advanced medical and surgical treatment options.",
                link: "/treatments/female-urology"
              },

              {
                title: "Pediatric Urology",
                img: Service9,
                desc: "Expert treatment for children with urological problems like phimosis, hypospadias, PUJO, reflux and undescended testis using safe pediatric procedures.",
                link: "/treatments/pediatric-urology"
              }
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white border rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition duration-300"
              >
                {/* image */}
                <div className="h-56 overflow-hidden">
                  <img
                    src={item.img}
                    className="w-full h-full object-cover hover:scale-110 transition duration-500"
                  />
                </div>

                {/* content */}
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {item.title}
                  </h3>

                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {item.desc}
                  </p>

                  <Link to={item.link} className="bg-primary hover:bg-primaryDark text-white px-6 py-2 rounded font-semibold">
                    Read More
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPOINTMENT SECTION */}
      <section
        id="appointment"
        className="py-24 relative overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `url(${AppointmentBanner})`,
        }}
      >
        {/* dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/50 "></div>

        <div className="relative max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center text-white">

          <div>
            <h2 className="text-4xl md:text-5xl font-black leading-tight">
              Book Appointment <br /> With Expert Urologist
            </h2>

            <p className="mt-6 text-white/90 text-lg">
              Get advanced urology & kidney treatment with latest technology
              and expert surgical care by Dr. Sachitanand Chaurasia.
            </p>

            <div className="flex gap-6 mt-10">
              <a
                href="tel:+917774956489"
                className="bg-white text-primary px-8 py-4 rounded-xl font-bold shadow-lg"
              >
                Call Now
              </a>
            </div>
          </div>

          {/* form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl text-black">
            <h3 className="text-2xl font-bold mb-6 text-center">
              Quick Appointment
            </h3>

            <div className="space-y-4">
              <input type="text" className="w-full border p-4 rounded-xl" placeholder="Full Name" />
              <input type="email" className="w-full border p-4 rounded-xl" placeholder="Email" />
              <input type="tel" className="w-full border p-4 rounded-xl" placeholder="Phone" />
              <textarea rows={3} className="w-full border p-4 rounded-xl" placeholder="Message" />

              <button className="w-full bg-primary text-white py-4 rounded-xl font-bold">
                Book Appointment
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-lightBg">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl font-black text-slate-900 mb-4">
            Patient Testimonials
          </h2>
          <p className="text-slate-600 mb-16">
            What our patients say about treatment & care
          </p>

          <div className="grid md:grid-cols-3 gap-8">

            {[
              {
                name: "Ramesh Sharma",
                city: "Bhilwara",
                text: "Best urologist in Bhilwara. My kidney stone surgery was painless and recovery was very fast."
              },
              {
                name: "Suresh Meena",
                city: "Chittorgarh",
                text: "Doctor is very polite and experienced. He explained everything clearly and treatment was excellent."
              },
              {
                name: "Amit Jain",
                city: "Jaipur",
                text: "Successful kidney transplant of my relative. Very professional and supportive doctor."
              }
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white p-8 rounded-3xl shadow-premium hover:shadow-glow transition duration-300 text-left"
              >
                {/* stars */}
                <div className="text-yellow-400 text-xl mb-4">★★★★★</div>

                {/* text */}
                <p className="text-slate-600 italic leading-relaxed">
                  “{item.text}”
                </p>

                {/* patient */}
                <div className="mt-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                    {item.name.charAt(0)}
                  </div>

                  <div>
                    <h4 className="font-bold text-slate-900">{item.name}</h4>
                    <p className="text-sm text-slate-500">{item.city}</p>
                  </div>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* EMERGENCY + APPOINTMENT */}
      <section className="py-20 bg-lightBg">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10">

          {/* emergency */}
          <div className="relative rounded-3xl overflow-hidden group">
            <img
              src="https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?q=80&w=1200"
              className="w-full h-[260px] object-cover group-hover:scale-110 transition duration-500"
            />

            <div className="absolute inset-0 bg-primary/70" />

            <div className="absolute inset-0 p-10 text-white flex flex-col justify-center">
              <h3 className="text-2xl font-bold">For Any Emergency Contact</h3>
              <p className="mt-2 text-white/90">24×7 Support</p>

              <a
                href="tel:+917774956489"
                className="mt-6 bg-primary px-6 py-3 rounded-full w-fit font-semibold"
              >
                +91 7774956489
              </a>
            </div>
          </div>

          {/* appointment */}
          <div className="relative rounded-3xl overflow-hidden group">
            <img
              src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=1200"
              className="w-full h-[260px] object-cover group-hover:scale-110 transition duration-500"
            />

            <div className="absolute inset-0 bg-secondary/80" />

            <div className="absolute inset-0 p-10 text-white flex flex-col justify-center">
              <h3 className="text-2xl font-bold">Make an Online Appointment</h3>
              <p className="mt-2 text-white/90">
                Schedule appointment with best urologist
              </p>

              <a href="#appointment" className="mt-6 bg-primary px-6 py-3 rounded-full w-fit font-semibold">
                Book Appointment
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">

          <h2 className="text-4xl font-black text-slate-900 mb-4 text-center">
            Frequently Asked Questions
          </h2>

          <p className="text-slate-600 mb-16 text-center">
            Find answers to common questions about urology treatments and appointments
          </p>

          {[
            {
              q: "How can I schedule an appointment with Dr. Sachitanand?",
              a: "You can call directly or fill appointment form to book consultation."
            },
            {
              q: "Who is best urologist in Bhilwara Rajasthan?",
              a: "Dr. Sachitanand Chaurasia is trusted urologist with years of experience."
            },
            {
              q: "What urology treatments available?",
              a: "Kidney stone, prostate, kidney transplant, bladder and all urology treatments."
            },
            {
              q: "Does doctor perform surgeries?",
              a: "Yes, advanced laparoscopic and laser urology surgeries available."
            },
            {
              q: "How many years experience doctor has?",
              a: "More than 8+ years experience with 10000+ successful surgeries."
            },
          ].map((item, i) => {
            return (
              <div key={i} className="border-b">
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex justify-between items-center py-5 font-semibold text-left"
                >
                  {item.q}
                  {open === i ? <Minus /> : <Plus />}
                </button>

                {open === i && (
                  <p className="pb-5 text-slate-600">{item.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA SECTION */}
      <section
        className="py-24 relative overflow-hidden bg-cover bg-center text-white text-center"
        style={{
          backgroundImage: `url(${ExperBanner})`,
        }}>

        <div className="absolute w-[400px] h-[400px] bg-secondary/40 blur-3xl rounded-full top-0 right-0" />

        <h2 className="text-5xl font-black">
          Need Expert Urology Treatment?
        </h2>

        <p className="mt-6 text-lg text-white/90">
          Book consultation with Dr. Sachitanand Chaurasia today
        </p>

        <a
          href="tel:+917774956489"
          className="inline-block mt-10 bg-white text-primary px-10 py-5 rounded-xl font-black text-lg shadow-2xl mr-4"
        >
          Call
        </a>
        <a
          href="#appointment"
          className="inline-block mt-10 bg-white text-primary px-10 py-5 rounded-xl font-black text-lg shadow-2xl"
        >
          Book Appointment
        </a>
      </section>

    </div>
  );
};

export default Home;
